/**
 * TechnicalCheckTabs — Iter88u (2026-02-19)
 *
 * Wrapper um WP- und PV-Technik-Check mit Sub-Tabs. Zeigt zusätzlich einen
 * „Installations-Brief"-Button oben rechts, der ein PDF für den Auftragnehmer
 * generiert (aus tech-check + Angeboten + Fotos).
 */
import { useState } from "react";
import { Flame, Sun, FileText, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import TechnicalCheckSection from "./TechnicalCheckSection";  // noqa: F401 — reserved for read-only fallback
import TechCheckPv from "./TechCheckPv";
import VCheckWpForm from "./VCheckWpForm";
import { HelpButton } from "@/components/HelpButton";

export default function TechnicalCheckTabs({ project, tech, photos = [], onUpdate }) {
  // Heuristik: Wenn Projekt schon PV-Konfigs hat → PV-Tab default. Sonst WP.
  const defaultTab = (project?._has_pv_configs || project?.type === "PV") ? "pv" : "wp";
  const subKey = `tech_subtab_${project?.id}`;
  const [tab, setTabState] = useState(() => {
    try { return sessionStorage.getItem(subKey) || defaultTab; } catch { return defaultTab; }
  });
  const setTab = (t) => {
    setTabState(t);
    try { sessionStorage.setItem(subKey, t); } catch { /* noop */ }
  };
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefResult, setBriefResult] = useState(null);
  const [sendDialog, setSendDialog] = useState({ open: false, to: "", sending: false });

  const generateBrief = async (scope) => {
    setBriefLoading(true);
    setBriefResult(null);
    try {
      const r = await api.post(`/projects/${project.id}/installation-brief`, { scope });
      setBriefResult(r.data);
      toast.success(`Installations-Brief erstellt (${scope.toUpperCase()})`);
      // Auto-Download
      if (r.data?.download_url) {
        window.open(r.data.download_url, "_blank");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Installations-Brief nicht erstellen");
    } finally {
      setBriefLoading(false);
    }
  };

  const sendBrief = async () => {
    if (!sendDialog.to?.trim().includes("@")) {
      toast.error("Bitte E-Mail-Adresse des Installateurs eintragen");
      return;
    }
    setSendDialog((d) => ({ ...d, sending: true }));
    try {
      const r = await api.post(`/projects/${project.id}/installation-brief/send`, {
        to: sendDialog.to.trim(),
        scope: tab,
      });
      toast.success(`Installations-Brief an ${r.data?.to} versendet`);
      setSendDialog({ open: false, to: "", sending: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
      setSendDialog((d) => ({ ...d, sending: false }));
    }
  };

  return (
    <div className="space-y-4">
      {/* Header mit Tabs + Brief-Action */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="inline-flex bg-[#101015] ring-1 ring-zinc-800 rounded-xl p-1 self-start">
          <button
            data-testid="tech-tab-wp"
            onClick={() => setTab("wp")}
            className={`h-10 px-4 rounded-lg inline-flex items-center gap-2 text-[13px] font-semibold transition ${
              tab === "wp" ? "bg-amber-500/20 text-amber-200 ring-1 ring-amber-500/40" : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            <Flame size={14} />
            Wärmepumpe
          </button>
          <button
            data-testid="tech-tab-pv"
            onClick={() => setTab("pv")}
            className={`h-10 px-4 rounded-lg inline-flex items-center gap-2 text-[13px] font-semibold transition ${
              tab === "pv" ? "bg-sky-500/20 text-sky-200 ring-1 ring-sky-500/40" : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            <Sun size={14} />
            Photovoltaik
          </button>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <HelpButton contextId="project.tech" size="sm" />
          <button
            data-testid="tech-brief-generate-btn"
            onClick={() => generateBrief(tab)}
            disabled={briefLoading}
            className="h-10 px-3 rounded-lg inline-flex items-center gap-2 text-[13px] font-semibold text-zinc-100 bg-zinc-800 hover:bg-zinc-700 ring-1 ring-zinc-700 transition disabled:opacity-60"
            title="Installations-Brief als PDF generieren"
          >
            {briefLoading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
            Brief für Installateur
          </button>
          <button
            data-testid="tech-brief-send-btn"
            onClick={() => setSendDialog({ open: true, to: "", sending: false })}
            className="h-10 px-3 rounded-lg inline-flex items-center gap-2 text-[13px] font-semibold text-emerald-100 bg-emerald-500/15 hover:bg-emerald-500/25 ring-1 ring-emerald-500/40 transition"
            title="Brief per E-Mail an Installateur senden"
          >
            <Send size={14} />
            Senden
          </button>
        </div>
      </div>

      {/* Content — Iter88zu Phase 2: WP-Tab nutzt jetzt VCheckWpForm (V-Check WP),
          PV-Tab bleibt vorerst beim alten TechCheckPv (Wahl B=b: PV-Analogon separat). */}
      {tab === "wp"
        ? <VCheckWpForm projectId={project.id} onFinalized={onUpdate} />
        : <TechCheckPv project={project} tech={tech} photos={photos} onUpdate={onUpdate} />
      }

      {/* Send-Dialog */}
      {sendDialog.open && (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => !sendDialog.sending && setSendDialog({ open: false, to: "", sending: false })}>
          <div className="bg-[#101015] ring-1 ring-zinc-800 rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[16px] font-bold text-zinc-100 mb-1">Installations-Brief senden</h3>
            <p className="text-[12px] text-zinc-400 mb-4">
              PDF wird direkt an den Installateur/Auftragnehmer gemailt (mit allen Fotos, Grundrissen und Aufgabenliste als Anhang).
            </p>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-zinc-400 mb-1">E-Mail Installateur</label>
            <input
              data-testid="tech-brief-send-to"
              type="email"
              value={sendDialog.to}
              onChange={(e) => setSendDialog((d) => ({ ...d, to: e.target.value }))}
              placeholder="installateur@firma.de"
              disabled={sendDialog.sending}
              className="h-11 w-full rounded-lg bg-[#09090B] border border-zinc-700 px-3 text-zinc-100 text-[14px] focus:border-[#00E575] outline-none"
            />
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setSendDialog({ open: false, to: "", sending: false })} disabled={sendDialog.sending}
                className="h-10 px-4 rounded-lg text-[13px] font-semibold text-zinc-300 hover:bg-zinc-800">
                Abbrechen
              </button>
              <button data-testid="tech-brief-send-confirm" onClick={sendBrief} disabled={sendDialog.sending}
                className="h-10 px-4 rounded-lg text-[13px] font-bold bg-[#00E575] text-[#09090B] hover:bg-[#00C964] inline-flex items-center gap-2 disabled:opacity-60">
                {sendDialog.sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Senden
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brief-Result Info */}
      {briefResult && !briefLoading && (
        <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3 text-[12.5px] text-emerald-100">
          Brief erstellt: <b>{briefResult.filename}</b> · {briefResult.pages || 0} Seite(n) · {briefResult.photos_count || 0} Foto(s)
          {briefResult.download_url && (
            <> · <a href={briefResult.download_url} target="_blank" rel="noreferrer" className="underline">Erneut öffnen</a></>
          )}
        </div>
      )}
    </div>
  );
}
